// Photos on posts and replies (migration 041).
//
// Files live in the PRIVATE `post-images` bucket, one folder per member, so
// every view needs a short-lived signed URL. See the migration header for why
// Supabase rather than Cloudflare.
//
// ⚠️ Every photo is RE-ENCODED through a canvas before upload. That is what
// strips EXIF — including GPS. DSPs photograph their workplaces and the people
// they support, and a home's coordinates riding along in a photo is the worst
// thing this feature could do. The original file never leaves the device.

import { supabase } from './supabase';

export const MAX_IMAGES = 4;
export const MAX_SOURCE_BYTES = 12 * 1024 * 1024;   // before re-encoding
export const ACCEPTED_TYPES = ['image/jpeg', 'image/png', 'image/webp', 'image/heic', 'image/heif'];

const FULL_MAX_EDGE = 1600;
const THUMB_MAX_EDGE = 600;
const FULL_QUALITY = 0.82;
const THUMB_QUALITY = 0.7;

// ── Decode + re-encode ────────────────────────────────────────

const loadBitmap = async (file) => {
  // createImageBitmap handles HEIC wherever the platform can decode it (iOS
  // converts on pick anyway via accept="image/*").
  if (typeof createImageBitmap === 'function') {
    try {
      return await createImageBitmap(file);
    } catch {
      /* fall through to <img> */
    }
  }
  const url = URL.createObjectURL(file);
  try {
    return await new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => resolve(img);
      img.onerror = () => reject(new Error('decode_failed'));
      img.src = url;
    });
  } finally {
    URL.revokeObjectURL(url);
  }
};

const scaleTo = (bitmap, maxEdge, quality) => {
  const w = bitmap.width;
  const h = bitmap.height;
  const scale = Math.min(1, maxEdge / Math.max(w, h));
  const width = Math.max(1, Math.round(w * scale));
  const height = Math.max(1, Math.round(h * scale));

  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  // White behind transparency: a PNG screenshot with an alpha channel would
  // otherwise come out with black edges once encoded as JPEG.
  ctx.fillStyle = '#FFFFFF';
  ctx.fillRect(0, 0, width, height);
  ctx.drawImage(bitmap, 0, 0, width, height);

  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => (blob ? resolve({ blob, width, height }) : reject(new Error('encode_failed'))),
      'image/jpeg',
      quality,
    );
  });
};

// Returns { full, thumb, width, height } — all JPEG, all EXIF-free.
export const prepareImage = async (file) => {
  const bitmap = await loadBitmap(file);
  const full = await scaleTo(bitmap, FULL_MAX_EDGE, FULL_QUALITY);
  const thumb = await scaleTo(bitmap, THUMB_MAX_EDGE, THUMB_QUALITY);
  bitmap.close?.();
  return { full: full.blob, thumb: thumb.blob, width: full.width, height: full.height };
};

// ── Upload ────────────────────────────────────────────────────

// Resolves to the shape sync_content_images expects:
//   { path, thumb, w, h, alt }
// The server re-checks that both paths sit in the caller's own folder and
// really exist, so a tampered client gets nothing extra.
export const uploadPostImage = async (userId, file) => {
  if (!userId) return { error: 'not_signed_in' };
  if (!ACCEPTED_TYPES.includes(file.type) && !file.type.startsWith('image/')) {
    return { error: 'unsupported_type' };
  }
  if (file.size > MAX_SOURCE_BYTES) return { error: 'too_large' };

  let prepared;
  try {
    prepared = await prepareImage(file);
  } catch {
    return { error: 'decode_failed' };
  }

  const id = crypto.randomUUID();
  const path = `${userId}/${id}.jpg`;
  const thumb = `${userId}/${id}_t.jpg`;

  const up = async (target, blob) => supabase.storage
    .from('post-images')
    .upload(target, blob, { contentType: 'image/jpeg', upsert: false });

  const { error: fullErr } = await up(path, prepared.full);
  if (fullErr) {
    console.error('post image upload:', fullErr);
    return { error: fullErr.message || 'upload_failed' };
  }

  const { error: thumbErr } = await up(thumb, prepared.thumb);
  if (thumbErr) {
    console.error('post image thumb upload:', thumbErr);
    // Don't leave the full-size file behind paying for nothing.
    await supabase.storage.from('post-images').remove([path]).catch(() => {});
    return { error: thumbErr.message || 'upload_failed' };
  }

  return { image: { path, thumb, w: prepared.width, h: prepared.height, alt: '' } };
};

// ── Signed URLs ───────────────────────────────────────────────
// A private bucket means every <img> needs a signed URL. Requests inside the
// same tick are batched into ONE createSignedUrls call, so a feed of cards
// costs one request rather than one per photo.

const SIGNED_TTL = 60 * 60;          // 1 hour
const REFRESH_MARGIN = 5 * 60 * 1000; // re-sign 5 min before expiry
const cache = new Map();              // path → { url, expires }
let queue = new Set();
let pending = null;

const flush = async () => {
  const paths = [...queue];
  queue = new Set();
  pending = null;
  if (!paths.length) return;

  const { data, error } = await supabase.storage
    .from('post-images')
    .createSignedUrls(paths, SIGNED_TTL);
  if (error) {
    console.error('createSignedUrls:', error);
    return;
  }
  const expires = Date.now() + SIGNED_TTL * 1000;
  for (const row of data ?? []) {
    if (row.signedUrl && row.path) cache.set(row.path, { url: row.signedUrl, expires });
  }
};

const schedule = () => {
  if (!pending) pending = Promise.resolve().then(flush);
  return pending;
};

export const cachedSignedUrl = (path) => {
  const hit = cache.get(path);
  return hit && hit.expires - Date.now() > REFRESH_MARGIN ? hit.url : null;
};

// Resolves a map of path → signed URL for everything asked for.
export const signImageUrls = async (paths) => {
  const wanted = [...new Set(paths.filter(Boolean))];
  const missing = wanted.filter((p) => !cachedSignedUrl(p));
  if (missing.length) {
    missing.forEach((p) => queue.add(p));
    await schedule();
  }
  const out = {};
  for (const p of wanted) {
    const hit = cache.get(p);
    if (hit) out[p] = hit.url;
  }
  return out;
};

// ── Deletion sweep ────────────────────────────────────────────
// Postgres can't delete a storage object, so the queue migration 041 fills is
// drained here: by the author right after deleting their own content, and by
// moderators when they open /admin (which is what covers a takedown from the
// reports queue). Best-effort by design — a failed pass leaves the rows for
// the next one, and `attempts` stops a bad path cycling forever.

export const sweepImageDeletions = async (limit = 50) => {
  const { data, error } = await supabase.rpc('claim_image_deletions', { p_limit: limit });
  if (error || !data?.length) return { removed: 0 };

  const paths = data.map((row) => row.object_path);
  const { error: rmErr } = await supabase.storage.from('post-images').remove(paths);
  if (rmErr) {
    console.error('sweep remove:', rmErr);
    return { removed: 0 };
  }
  const { error: markErr } = await supabase.rpc('mark_image_deletions_done', {
    p_ids: data.map((row) => row.deletion_id),
  });
  if (markErr) console.error('mark_image_deletions_done:', markErr);
  return { removed: paths.length };
};

// Saves the image set for a post or reply. Called AFTER the post exists — a
// failed attach must never cost the member their post (039's ordering rule).
export const syncContentImages = async ({ postId = null, replyId = null, images = [] }) => {
  const { error } = await supabase.rpc('sync_content_images', {
    p_post_id: postId,
    p_reply_id: replyId,
    p_images: images.map(({ path, thumb, w, h, alt }) => ({ path, thumb, w, h, alt: alt || null })),
  });
  return error ? { error } : {};
};

export const imageErrorMessage = (code) => {
  const m = String(code ?? '');
  if (m.includes('image_quota_exceeded')) return 'Daily photo limit reached (40). Try again tomorrow.';
  if (m.includes('too_many_images')) return `Up to ${MAX_IMAGES} photos.`;
  if (m.includes('not_verified')) return 'Verification required to add photos.';
  if (m.includes('path_not_owned') || m.includes('already_attached')) return 'That photo could not be attached.';
  if (m.includes('file_missing')) return 'That photo finished uploading incorrectly — remove and try again.';
  if (m.includes('unsupported_type')) return 'Photos only (JPEG, PNG, WebP or HEIC).';
  if (m.includes('too_large')) return 'That photo is too large (12 MB max).';
  if (m.includes('decode_failed')) return "That file couldn't be read as a photo.";
  if (m.includes('exceeded the maximum allowed size')) return 'That photo is too large after processing.';
  if (m.includes('row-level security') || m.includes('Unauthorized') || m.includes('403')) {
    return 'Photos need a verified account, and stop at 40 a day.';
  }
  if (m.includes('Bucket not found')) return 'Photo storage is not set up yet (bucket missing).';
  if (m.includes('mime') || m.includes('content type')) return 'That image type is not allowed.';
  if (m.includes('Payload too large') || m.includes('413')) return 'That photo is too large after processing.';
  // Anything unmapped keeps its real text: a generic "try again" for a
  // misconfiguration just sends people round the same loop.
  return m ? `Could not add that photo: ${m}` : 'Could not add that photo. Please try again.';
};
